import { Injectable } from "@angular/core"
import { NavigationEnd, Router } from "@angular/router"
import { filter } from "rxjs/operators"
import { environment } from "../../environments/environment"
import { ApiService } from "./api.service"
import { AuthService } from "./auth.service"
import { ProfileService } from "./profile.service"

declare global {
  interface Window {
    dataLayer?: unknown[]
    gtag?: (...args: unknown[]) => void
  }
}

const GA_MEASUREMENT_ID = "G-YBVNP7HM96"

/**
 * GA4 for the app subdomain only (G-YBVNP7HM96).
 * Sends numeric internal user_id and page paths — never name/email/phone.
 */
@Injectable({ providedIn: "root" })
export class GoogleAnalyticsService {
  private started = false
  private currentUserId: string | null = null
  private lastTrackedKey: string | null = null

  constructor(
    private router: Router,
    private profile: ProfileService,
    private auth: AuthService,
    private api: ApiService,
  ) {}

  init(): void {
    if (this.started) return
    this.started = true

    this.applyUserId(this.resolveInternalUserId())
    this.trackCurrentPage()

    this.profile.currentUser$.subscribe((user) => {
      const userId = this.toInternalUserId(user?.userid)
      if (userId) this.applyUserId(userId)
    })

    this.auth.currentUser.subscribe((session) => {
      if (!session) {
        this.applyUserId(null)
        return
      }
      const userId = this.resolveInternalUserId()
      if (userId) this.applyUserId(userId)
    })

    this.router.events.pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd)).subscribe(() => {
      this.trackCurrentPage()
    })
  }

  private applyUserId(userId: string | null): void {
    if (userId === this.currentUserId) return
    this.currentUserId = userId

    if (this.gtag) {
      this.gtag("set", { user_id: userId || undefined })
      this.gtag("config", GA_MEASUREMENT_ID, {
        user_id: userId || undefined,
        send_page_view: false,
      })
    }

    if (this.started) this.trackCurrentPage()
  }

  private trackCurrentPage(): void {
    const pagePath = this.pagePathFromUrl(this.router.url)
    const key = `${this.currentUserId || ""}|${pagePath}`
    if (key === this.lastTrackedKey) return
    this.lastTrackedKey = key
    this.trackPageView(pagePath)
    if (this.currentUserId) this.recordPageView(pagePath)
  }

  private trackPageView(pagePath: string): void {
    if (!this.gtag) return

    const payload: Record<string, string> = {
      page_path: pagePath,
      page_location: `${window.location.origin}${pagePath}`,
      page_title: document.title || "",
      send_to: GA_MEASUREMENT_ID,
    }
    if (this.currentUserId) {
      payload["user_id"] = this.currentUserId
    }

    this.gtag("event", "page_view", payload)
  }

  private recordPageView(pagePath: string): void {
    this.api.authenticatedPost(`${environment.apiUrl}/me/page-views`, { page_path: pagePath }).subscribe({
      error: () => undefined,
    })
  }

  private pagePathFromUrl(url: string): string {
    const path = (url || "/").split("?")[0].split("#")[0]
    return path.startsWith("/") ? path : `/${path}`
  }

  private resolveInternalUserId(): string | null {
    const fromProfile = this.toInternalUserId(this.profile.getUserId())
    if (fromProfile) return fromProfile

    const info = this.auth.getCurrentUserInfo() as Record<string, unknown> | null
    return this.toInternalUserId(info?.["userid"] ?? info?.["user_id"] ?? info?.["id"])
  }

  private toInternalUserId(raw: unknown): string | null {
    const n = typeof raw === "number" ? raw : Number(raw)
    if (!Number.isFinite(n) || n <= 0) return null
    return String(n)
  }

  private get gtag(): ((...args: unknown[]) => void) | undefined {
    return typeof window !== "undefined" ? window.gtag : undefined
  }
}
